/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, inject, signal} from '@angular/core';
import {UIRouter} from '@uirouter/core';
import * as _ from 'lodash';
import template from './folio-edit-page.component.html';

import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {advertiserFromNavigation} from '../../components/util/navigation-context';
import {TeamApiService} from '../admin/team/team-api.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {SearchApiService} from '../common/controller/search-api.service';
import {NavigationHistoryService} from '../../components/util/navigation-history.service';
import {deferSignalStateWrite} from '../common/util/template-state';
import {FolioApiService, FolioModel} from './folio-api.service';
import {AdvertisingSharedModule} from '../advertising/advertising-shared.module';

@Component({
  standalone: true,
  imports: [AdvertisingSharedModule],
  selector: 'adn-folio-edit-page',
  template
})
export class FolioEditPageComponent extends SignalAdminResourceEditPageBase<FolioModel> {
  readonly teams = signal<any[]>([]);
  showTeams = false;
  readonly advertiserResults = signal<any[]>([]);
  advertiserInfo = true;
  readonly chartCurrency = signal<any>(null);
  canSeeReports = false;
  readonly showingTraffic = signal(false);
  readonly showingLocation = signal(false);
  perms: any = {};
  readonly blockSubmit = signal(false);
  readonly activeTab = signal<'charts' | 'traffic' | 'location'>('charts');

  constructor(
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly teamApi: TeamApiService,
    private readonly searchApi: SearchApiService,
    private readonly uiRouter: UIRouter,
    private readonly navigationHistory: NavigationHistoryService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    super(inject(FolioApiService), 'folio');

    this.perms = LocalUserPermissions.getAllVisPermissions();
    this.canSeeReports = LocalUserPermissions.getAnyVisibility().RUN_REPORTS === true || this.perms.AX_ADVERTISER;
    this.showTeams = this.localNetworkInfo.getInfo('teamsCount') > 1 || !LocalNetworkProfile.isPureMarketplacePlatform();

    const isPureMarketplace = LocalNetworkProfile.isPureMarketplacePlatform();
    this.advertiserInfo = !isPureMarketplace || !!this.uiRouter.globals.params.isNew;

    this.teamApi.listOptions().subscribe((page) => {
      // [] → results flips *ngFor on the team <select> mid-CD (NG0100).
      deferSignalStateWrite(() => {
        const allTeams = page.results || [];
        const isViewAllObjects = this.LocalUserPermissions.hasNetwork('VIEW_ALL_OBJECTS');
        const teams = isViewAllObjects ? allTeams : _.filter(allTeams, (g: any) => {
          return this.LocalUserPermissions.isPartOf(g.network.tagId || g.network.id, g.id);
        });
        this.teams.set(teams);
        if (_.get(this, ['editingModel', 'team', 'id'])) {
          const hydratedTeam = _.find(allTeams, (t: any) => this.editingModel.team.id === t.id);
          if (hydratedTeam) {
            this.editingModel.team = hydratedTeam;
          }
        }
        if (allTeams.length === 1 && teams.length === 1 && !this.editingModel.team) {
          this.editingModel.team = allTeams[0];
        }
      });
    });
  }

  advertiserSearch = (searchElement: AdnSelectSearchElement): void => {
    this.searchApi.search({q: searchElement.search, types: 'Advertiser'}).subscribe((data) => {
      deferSignalStateWrite(() => this.advertiserResults.set(data.searchResults || []));
    });
  };

  onFieldChange(): void {
    this.markExternalFormDirty();
  }

  selectTab(tab: ReturnType<FolioEditPageComponent['activeTab']>): void {
    this.activeTab.set(tab);
    if (tab === 'traffic') {
      this.showingTraffic.set(true);
    } else if (tab === 'location') {
      this.showingLocation.set(true);
    }
  }

  get showAdvertiserField(): boolean {
    return this.perms.MANAGE_ADVERTISING === true || this.perms.AX_ADVERTISER === true;
  }

  onFolioCreativesAdvertiserFixed(saved: any): void {
    if (saved) {
      this.saveDirect(saved);
    }
  }

  protected override initialiseModel(model: FolioModel): FolioModel {
    const initialised = super.initialiseModel(model);
    const advertiser = advertiserFromNavigation(this.navigationHistory, this.uiRouter.globals.params);
    if (this.isNew && advertiser) {
      initialised.advertiser = advertiser;
    }
    this.blockSubmit.set(!!this.uiRouter.globals.params.readOnly);
    return initialised;
  }

  protected override afterEditingModelReset(model: FolioModel): void {
    this.chartCurrency.set(_.get(model, ['objectives', 'BUDGET', 'currency']));
  }

  protected override prepareForSubmit(model: FolioModel): void {
    if (!_.get(model.advertiser, 'id')) {
      model.advertiser = null;
    }
    if (this.isNew) {
      this.localNetworkInfo.increment('folioCount');
    }
  }
}
